// src/components/Pricing/CreditPackCard.tsx
// One-time credit pack with quantity selector
import React, { useState } from 'react';

interface CreditPackCardProps {
  tierCode: string;
  priceInr: number;
  priceUsd: number;
  creditsPerPack: number;
  currency: 'INR' | 'USD';
  onPurchase?: (quantity: number) => void;
  className?: string;
}

const CreditPackCard: React.FC<CreditPackCardProps> = ({
  tierCode,
  priceInr,
  priceUsd,
  creditsPerPack,
  currency,
  onPurchase,
  className = '',
}) => {
  const [quantity, setQuantity] = useState(1);
  const unitPrice = currency === 'INR' ? priceInr : priceUsd;
  const symbol = currency === 'INR' ? '\u20B9' : '$';
  const totalPrice = unitPrice * quantity;
  const totalCredits = creditsPerPack * quantity;

  const inc = () => setQuantity((q) => Math.min(q + 1, 50));
  const dec = () => setQuantity((q) => Math.max(q - 1, 1));

  return (
    <div
      className={`relative border-2 border-blue-300 dark:border-blue-600 rounded-2xl p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/10 hover:shadow-xl transition-all flex flex-col ${className}`}
    >
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
          Top Up Credits
        </span>
      </div>

      <div className="text-center flex flex-col flex-grow">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 mt-2">
          Credit Pack
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {creditsPerPack} credits &mdash; expires with your subscription
        </p>

        {/* Price */}
        <div className="mb-4">
          <div className="flex items-baseline justify-center">
            <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
              {symbol}{totalPrice.toLocaleString()}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            One-time payment
          </p>
        </div>

        {/* Quantity selector */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            QUANTITY
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={dec}
              disabled={quantity <= 1}
              className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 font-bold text-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              aria-label="Decrease quantity"
            >
              &minus;
            </button>
            <div className="min-w-[3rem] px-4 py-2 bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-600 rounded-lg font-bold text-lg text-gray-900 dark:text-white">
              {quantity}
            </div>
            <button
              onClick={inc}
              disabled={quantity >= 50}
              className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 font-bold text-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>

        {/* Total credits */}
        <div className="mb-6 py-3 bg-white/60 dark:bg-gray-800/40 rounded-xl border border-blue-200 dark:border-blue-700">
          <p className="text-xs text-gray-600 dark:text-gray-400">You get</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
            {totalCredits.toLocaleString()} credits
          </p>
        </div>

        {/* CTA */}
        <button
          onClick={() => onPurchase?.(quantity)}
          className="w-full py-3 px-6 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transform hover:-translate-y-0.5 transition-all mt-auto"
        >
          Buy {totalCredits.toLocaleString()} Credits &mdash; {symbol}{totalPrice.toLocaleString()}
        </button>
      </div>
    </div>
  );
};

export default CreditPackCard;
